import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { CheckCircle, Flame, Calendar, Clock, Phone, Mail, Home, User, CreditCard, AlertTriangle } from "lucide-react";
import Footer from "../components/Footer";
import { formatDate } from "../utils/dateFormat";

const BBQConfirmationPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await axios.get(`${API}/bbq/bookings/${bookingId}`);
        setBooking(res.data);
      } catch (error) {
        console.error("Failed to fetch booking:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl font-bold">Loading...</div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold text-red-600">Booking not found</p>
          <button onClick={() => navigate('/bbq')} className="mt-4 brutal-btn-primary">
            Back to BBQ Booking
          </button>
        </div>
      </div>
    );
  }

  const pitLabel = booking.pit_selection === 'both' ? 'Both Pits (Pit 1 & Pit 2)' : 
                   booking.pit_selection === 'pit1' ? 'Pit 1' : 'Pit 2';

  return (
    <div className="min-h-screen bg-orange-50">
      {/* Header */}
      <header className="bg-orange-500 text-white border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-2xl sm:text-3xl font-syne font-bold">BOOKING CONFIRMED!</h1>
          <p className="text-white/90 mt-2">Your <span className="notranslate" translate="no">BBQ pit</span> reservation has been received</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Booking Code */}
        <div className="bg-orange-600 border-4 border-black p-6 mb-6 text-center">
          <p className="text-sm font-bold text-orange-100 mb-1">YOUR BOOKING REFERENCE</p>
          <p className="text-4xl sm:text-5xl font-mono font-bold text-white notranslate" translate="no">{booking.booking_code}</p>
        </div>

        {/* Booking Details */}
        <div className="bg-white border-4 border-black p-6 mb-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Flame className="text-orange-500" /> Booking Details
          </h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 border-2 border-gray-200">
              <Calendar className="text-orange-500" size={20} />
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-bold">{formatDate(booking.date)}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-gray-50 border-2 border-gray-200">
              <Clock className="text-orange-500" size={20} />
              <div>
                <p className="text-sm text-gray-500">Time Slot</p>
                <p className="font-bold">{booking.slot_type === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-gray-50 border-2 border-gray-200">
              <Flame className="text-orange-500" size={20} />
              <div>
                <p className="text-sm text-gray-500"><span className="notranslate" translate="no">Pit(s)</span> Reserved</p>
                <p className="font-bold notranslate" translate="no">{pitLabel}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-orange-50 border-2 border-orange-300">
              <CreditCard className="text-orange-600" size={20} />
              <div>
                <p className="text-sm text-orange-600">Amount Due</p>
                <p className="font-bold text-xl text-orange-600">${booking.price}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-white border-4 border-black p-6 mb-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <User className="text-orange-500" /> Your Details
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <User size={16} className="text-gray-400" />
              <span className="notranslate" translate="no">{booking.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Home size={16} className="text-gray-400" />
              <span className="notranslate" translate="no">{booking.block_unit}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={16} className="text-gray-400" />
              <span className="notranslate" translate="no">{booking.mobile}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-gray-400" />
              <span className="notranslate" translate="no">{booking.email}</span>
            </div>
          </div>
        </div>

        {/* Payment Instructions */}
        <div className="bg-white border-4 border-black p-6 mb-6">
          <h2 className="text-lg font-bold mb-4 text-red-600">⚠️ PAYMENT REQUIRED</h2>
          
          <div className="bg-amber-50 border-2 border-amber-400 p-4 mb-4">
            <p className="font-bold text-amber-800 mb-2">Please ensure prompt payment after receiving the payment link from Admin.</p>
            <p className="text-sm text-amber-700">Please ensure prompt payment. Strictly no refunds.</p>
          </div>
          
          <div className="space-y-2 text-sm">
            <p><strong>Payment Method:</strong> onePA</p>
            <p className="text-sm text-gray-600 mt-1">An Admin will contact you with the onePA payment link.</p>
            <p className="mt-2"><strong>Reference:</strong> <span className="font-mono font-bold text-orange-600 notranslate" translate="no">{booking.booking_code}</span></p>
            <p className="text-sm mt-1"><strong>All payments/transactions must be made via onePA only.</strong></p>
          </div>
          
          <div className="mt-4 p-3 bg-orange-100 border-2 border-orange-400">
            <p className="font-bold text-orange-700">
              An Admin will verify your payment and send confirmation.
            </p>
          </div>
        </div>

        {/* No Refund Notice */}
        <div className="bg-red-50 border-4 border-red-400 p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-bold text-red-800">No Refund Policy</p>
              <p className="text-sm text-red-700">Strictly no refunds.</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/bbq')}
            className="flex-1 py-3 font-bold border-2 border-black bg-white hover:bg-gray-100"
          >
            BOOK ANOTHER
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-1 py-3 font-bold border-2 border-black bg-orange-500 text-white hover:bg-orange-600"
          >
            BACK TO HOME
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BBQConfirmationPage;
